import { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Link } from "react-router-dom";
import "./App.css";
import PrivacyPolicy from "./PrivacyPolicy";

// Footer component with a Privacy Policy and contact link
const Footer = () => {
  const [isBottom, setIsBottom] = useState(false);

  useEffect(() => {
    let timeoutId;

    const handleScroll = () => {
      // Clear any pending update to avoid rapid toggling
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        if (
          window.innerHeight + window.pageYOffset >=
          document.body.offsetHeight - 200
        ) {
          setIsBottom(true);
        } else {
          setIsBottom(false);
        }
      }, 100); // adjust debounce delay if needed
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  if (!isBottom) return null;

  return (
    <footer className="bg-white text-gray-700 py-4 border-t mt-6">
      <div className="text-xs text-right">
        <div>
          <a
            href="https://www.buymeacoffee.com/dvizcarra"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-500 hover:underline"
          >
            🐶 Buy me a treat! 🦴
          </a>{" "}
          |{" "}
          <a
            href="mailto:danielvizcarra@protonmail.com"
            className="text-blue-500 hover:underline"
          >
            Email
          </a>{" "}
          |{" "}
          <Link to="/privacy" className="text-blue-500 hover:underline">
            Privacy Policy
          </Link>
        </div>
        <div className="mt-1">Made with ☕</div>
      </div>
    </footer>
  );
};


// MainApp holds your current app content with only the dedicated Ping tool active
const MainApp = () => {
  const [ipInfo, setIpInfo] = useState(null);
  const [error, setError] = useState(null);
  const [browserInfo, setBrowserInfo] = useState({
    platform: "",
    language: "",
    userAgent: "",
  });

  const [pingTarget, setPingTarget] = useState("");
  const [pingOutput, setPingOutput] = useState("");
  const [pingLoading, setPingLoading] = useState(false);

  useEffect(() => {
    fetch("https://api.ipdata.co?api-key=9f3973d3dbb84e45bbcfb71a368e96652f238e8fd5c1fd431ce8138c")
      .then((res) => {
        if (!res.ok) throw new Error(`API error: ${res.status}`);
        return res.json();
      })
      .then((data) => setIpInfo(data))
      .catch((err) => {
        console.error("Failed to fetch IP data:", err);
        setError("Unable to fetch IP info");
      });

    setBrowserInfo({
      platform: navigator.platform,
      language: navigator.language,
      userAgent: navigator.userAgent,
    });
  }, []);

  const copyToClipboard = () => {
    if (ipInfo?.ip) navigator.clipboard.writeText(ipInfo.ip);
  };

  const handleRealPing = async () => {
    if (!pingTarget) return;
    setPingLoading(true);
    setPingOutput("");

    try {
      const response = await fetch(
        `https://us-central1-ipbark.cloudfunctions.net/pingTarget?target=${pingTarget}`
      );
      const data = await response.json();
      setPingOutput(data.result || "No output returned.");
    } catch (err) {
      console.error("Ping failed:", err);
      setPingOutput("❌ Error reaching backend ping function.");
    }

    setPingLoading(false);
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6 text-center">
      {/* Header */}
      <header className="mb-10 flex flex-col items-center justify-center">
        <div className="flex items-center gap-3">
          <img src="/ipbark-logo.png" alt="IPBark Logo" className="w-40 h-30" />
          <h1 className="text-5xl font-extrabold text-blue-600">IPBark</h1>
        </div>
        <p className="text-lg mt-2 text-gray-600">
          Simple, fun, privacy-first network diagnostics
        </p>
      </header>

{/* IP Info */}
<section className="bg-white max-w-md mx-auto p-6 rounded-xl shadow-md mb-10">
  <p className="text-xl font-medium">Your Public IP:</p>
  <div className="mt-2 border-4 border-blue-500 bg-blue-50 text-blue-800 py-4 px-6 rounded-xl shadow-sm flex flex-col md:flex-row items-center">
    <p className="text-4xl font-mono font-bold text-center md:text-left flex-1">
      {error ? "Error" : ipInfo?.ip || "Loading..."}
    </p>
    <button
      onClick={copyToClipboard}
      className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-1 text-sm rounded mt-2 md:mt-0 md:ml-4"
    >
      Copy
    </button>
  </div>

  {ipInfo && !error && (
    <div className="mt-6 text-left text-sm text-gray-700 space-y-2">
      <p>
        <strong>IP Version:</strong> {ipInfo && ipInfo.ip.includes(':') ? "IPv6" : "IPv4"}
      </p>
      <p><strong>City:</strong> {ipInfo.city}</p>
      <p><strong>Region:</strong> {ipInfo.region}</p>
      <p><strong>Country:</strong> {ipInfo.country_name}</p>
      <p><strong>Org / ISP:</strong> {ipInfo.asn?.name || ipInfo.org}</p>
      <p><strong>ASN Domain:</strong> {ipInfo.asn?.domain}</p>
      <p><strong>ASN:</strong> {ipInfo.asn?.asn}</p>
      <p><strong>BGP Prefix:</strong> {ipInfo.asn?.route}</p>
      <p><strong>Timezone:</strong> {ipInfo.time_zone?.name}</p>
      <p><strong>Latitude:</strong> {ipInfo.latitude}</p>
      <p><strong>Longitude:</strong> {ipInfo.longitude}</p>
    </div>
  )}

  {browserInfo && (
    <div className="mt-6 text-left text-sm text-gray-700 space-y-2 border-t pt-4">
      <p><strong>Platform:</strong> {browserInfo.platform}</p>
      <p><strong>Language:</strong> {browserInfo.language}</p>
      <p><strong>User Agent:</strong> {browserInfo.userAgent}</p>
    </div>
  )}

  {ipInfo?.latitude && ipInfo?.longitude && (
    <div className="mt-6">
      <iframe
        title="Map Location"
        width="100%"
        height="200"
        className="rounded-lg shadow-sm border"
        src={`https://www.openstreetmap.org/export/embed.html?bbox=${ipInfo.longitude - 0.02},${ipInfo.latitude - 0.02},${ipInfo.longitude + 0.02},${ipInfo.latitude + 0.02}&layer=mapnik&marker=${ipInfo.latitude},${ipInfo.longitude}`}
      ></iframe>
    </div>
  )}

  {error && <p className="mt-4 text-red-500 font-medium">{error}</p>}
</section>


      {/* Dedicated Ping Tool */}
      <section className="bg-white max-w-md mx-auto p-6 rounded-xl shadow-md mb-12">
        <h2 className="text-xl font-bold text-gray-800 mb-4">📡 Real Ping (Server-Side)</h2>
        <input
          type="text"
          placeholder="Enter domain or IP (e.g., google.com)"
          value={pingTarget}
          onChange={(e) => setPingTarget(e.target.value)}
          className="w-full px-4 py-2 border rounded-lg mb-3"
        />
        <button
          onClick={handleRealPing}
          disabled={pingLoading}
          className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded disabled:opacity-50"
        >
          {pingLoading ? "Pinging..." : "Ping"}
        </button>
        {pingOutput && (
          <div className="mt-4 text-left">
            <pre className="bg-gray-100 text-sm p-4 rounded overflow-x-auto whitespace-pre-wrap">
              {pingOutput}
            </pre>
            <button
              onClick={() => navigator.clipboard.writeText(pingOutput)}
              className="mt-2 text-sm bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded"
            >
              Copy Results
            </button>
          </div>
        )}
      </section>

      {/* Extra whitespace with message */}
      <div className="my-12 flex items-center justify-center space-x-4">
        <p className="text-xl font-semibold text-gray-700">
        More tools coming soon – stay tuned!
        </p>
        <img src="/ipbark-dad-logo.png" alt="IPBark Dad Logo" className="h-40" />
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

// Wrap your MainApp and PrivacyPolicy routes using React Router
function AppRouter() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<MainApp />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
      </Routes>
    </Router>
  );
}

export default AppRouter;
